import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Spinner, Alert, Form, Table } from 'react-bootstrap';
import {
  useGetAllSubjectsQuery,
  useLazyGetSubjectBioResultsQuery,
  useLazyGetSubjectDetailsQuery,
} from '../../store/api/analysisApi';
import Plot from 'react-plotly.js';
import { BIOMARKERS } from '../../config';
import * as Plotly from 'plotly.js';

// --- BulletChart component ---
const BulletChart = ({ title, data, value }: { title: string, data: any, value: number }) => {
  if (!data || value === null || data[0.1] === null) return <div style={{height: '60px', lineHeight: '60px', textAlign: 'center', color: '#999'}}>Not enough data</div>;
    
  const rangeMin = Math.min(data[0.1], value) * 0.95;
  const rangeMax = Math.max(data[0.9], value) * 1.05;

  const layout: Partial<Plotly.Layout> = {
    xaxis: { range: [rangeMin, rangeMax] },
    yaxis: { showticklabels: false },
    height: 60,
    margin: { t: 20, b: 20, l: 10, r: 10 },
    barmode: 'stack',
    showlegend: false
  };

  const plotData: Plotly.Data[] = [
        { type: 'bar', x: [data[0.9] - data[0.1]], y: [title], base: data[0.1], orientation: 'h', marker: { color: 'rgba(204, 204, 204, 0.5)' }, hoverinfo: 'none'} as any,
        { type: 'bar', x: [data[0.75] - data[0.25]], y: [title], base: data[0.25], orientation: 'h', marker: { color: 'rgba(160, 160, 160, 0.7)' }, hoverinfo: 'none'} as any,
        { type: 'scatter', x: [data[0.5]], y: [title], mode: 'markers', marker: { color: 'black', size: 12, symbol: 'line-ns-open' }, name: `Median: ${data[0.5]?.toFixed(2)}`},
        { type: 'scatter', x: [value], y: [title], mode: 'markers', marker: { color: '#d9534f', size: 14, symbol: 'line-ns', line: { width: 3 } }, name: `Your Value: ${value?.toFixed(2)}`},
  ];

  return <Plot data={plotData} layout={layout} style={{ width: '100%' }} config={{ staticPlot: true }} />;
};

// --- BioResultsTable component ---
const BioResultsTable = ({ subjectId }: { subjectId: string }) => {
  const [triggerBioResults, { data: results = [], isFetching: loading }] =
    useLazyGetSubjectBioResultsQuery();

  useEffect(() => {
    if (subjectId) {
      triggerBioResults(subjectId);
    }
  }, [subjectId, triggerBioResults]);

  if (loading) return <div className="text-center"><Spinner animation="border" size="sm" /></div>;
  if (!results || results.length === 0) return <p>No biological report data available for this subject.</p>;

  return (
    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
      <Table striped bordered hover size="sm">
        <thead><tr><th>Test Name</th><th>Value</th><th>Unit</th><th>Status</th></tr></thead>
        <tbody>
          {results.map((res) => (
            <tr key={res.key}>
              <td>{res.name}</td><td>{res.value}</td><td>{res.unit}</td>
              <td>{res.out_of_threshold ? <span className="text-danger">● Out of Range</span> : <span className="text-success">● Normal</span>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// --- Main Explorer Component ---
const IndividualExplorer: React.FC = () => {
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [subjectData, setSubjectData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { data: subjectList = [], isLoading: loadingSubjects, error: subjectsError } = useGetAllSubjectsQuery();
  const [triggerSubjectDetails] = useLazyGetSubjectDetailsQuery();

  useEffect(() => {
    if (!selectedSubject) {
      setSubjectData(null);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await triggerSubjectDetails(selectedSubject).unwrap();
        setSubjectData(data);
      } catch (err) {
        setError("Failed to fetch subject details.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedSubject, triggerSubjectDetails]);
    
  return (
    <Card>
      <Card.Body>
        <Card.Title>Individual Subject Explorer</Card.Title>
        <Form.Group>
          <Form.Label>Select Subject</Form.Label>
          <Form.Select value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
            <option value="">Select...</option>
            {subjectList.map(s => <option key={s.SubjectID} value={s.SubjectID}>{s.SubjectID}</option>)}
          </Form.Select>
        </Form.Group>
                
        {(loading || loadingSubjects) && <div className="text-center p-5"><Spinner animation="border" /></div>}
        {(error || subjectsError) && <Alert variant="danger" className="mt-3">{error || 'Failed to fetch subject list.'}</Alert>}

        {subjectData && !loading && (
          <div className="mt-4">
            <hr/>
            <h3>Details for: {subjectData.details.SubjectID}</h3>
            <Row className="mb-3 g-3">
              <Col><Card bg="primary" text="white" className="text-center h-100"><Card.Body><h5>{subjectData.details.inflammatory_zscore?.toFixed(2)}</h5><p className="mb-0">Subject&apos;s Z-Score</p></Card.Body></Card></Col>
              <Col><Card bg="secondary" text="white" className="text-center h-100"><Card.Body><h5>{subjectData.metrics.batchMeanZScore?.toFixed(2)}</h5><p className="mb-0">Batch Mean Z-Score</p></Card.Body></Card></Col>
              <Col><Card bg="success" text="white" className="text-center h-100"><Card.Body><h5>{subjectData.metrics.globalMeanZScore?.toFixed(2)}</h5><p className="mb-0">Global Mean Z-Score</p></Card.Body></Card></Col>
            </Row>
            <Row className="g-3">
              <Col><Card bg="light"><Card.Body><strong>Age:</strong> {subjectData.details.Age || 'N/A'}</Card.Body></Card></Col>
              <Col><Card bg="light"><Card.Body><strong>Gender:</strong> {subjectData.details.Gender || 'N/A'}</Card.Body></Card></Col>
              <Col><Card bg="light"><Card.Body><strong>Smoking:</strong> {subjectData.details.Smoking || 'N/A'}</Card.Body></Card></Col>
              <Col><Card bg="light"><Card.Body><strong>BMI:</strong> {subjectData.details.BMI?.toFixed(1) || 'N/A'}</Card.Body></Card></Col>
            </Row>

            <Row className="mt-4">
              <Col md={6}>
                <h5>Comparison to Global Population</h5>
                {BIOMARKERS.map(marker => (
                  <div key={marker}>
                    <strong>{marker}</strong>
                    {/* --- THIS IS THE FIX --- */}
                    <BulletChart
                      title={marker}
                      data={subjectData.plots.profileVsGlobal[marker]}
                      value={subjectData.details[marker]}
                    />
                  </div>
                ))}
              </Col>
              <Col md={6}>
                <h5>Comparison to Batch &apos;{subjectData.details.BatchID}&apos;</h5>
                {BIOMARKERS.map(marker => (
                  <div key={marker}>
                    <strong>{marker}</strong>
                    {/* --- THIS IS THE FIX --- */}
                    <BulletChart
                      title={marker}
                      data={subjectData.plots.profileVsBatch[marker]}
                      value={subjectData.details[marker]}
                    />
                  </div>
                ))}
              </Col>
            </Row>
                        
            <Row className="mt-4">
              <Col>
                <h5>Personal Z-Score History</h5>
                {subjectData.plots.zscoreHistory.length > 0 ? (
                  <Plot
                    data={[{ x: subjectData.plots.zscoreHistory.map((h: any) => h.stageName.replace(/_/g, ' ')), y: subjectData.plots.zscoreHistory.map((h: any) => h.zScore), type: 'scatter', mode: 'lines+markers'}]}
                    layout={{ title: {text: `Z-Score Evolution for ${selectedSubject}`}, xaxis: {title: {text: 'Cumulative Stage'}}, yaxis: {title: {text: 'Z-Score'}}}}
                    style={{ width: '100%' }} useResizeHandler={true}
                  />
                ) : <p>No Z-Score history available for this subject.</p>}
              </Col>
            </Row>
                        
            <Row className="mt-4"><Col>
              <Card><Card.Header as="h5">Biological Lab Report</Card.Header>
                <Card.Body>
                  <BioResultsTable subjectId={selectedSubject} />
                </Card.Body>
              </Card>
            </Col></Row>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default IndividualExplorer;